/**
 * Product analytics (docs/specs/GAP_CLOSING_PLAN.md — M0.1 / M0.2).
 *
 * One module owns the whole taxonomy. Nothing else in the app should import
 * posthog-js directly.
 *
 *   - No-op unless VITE_POSTHOG_KEY is set (local dev, previews, tests).
 *   - posthog-js is loaded with a dynamic import, so it costs nothing in the
 *     main bundle and nothing at all when analytics is off.
 *   - Most funnel events are derived from successful API calls by one axios
 *     interceptor (API_EVENT_RULES below) instead of track() calls scattered
 *     through 30 pages — a new page that creates a scene is instrumented for
 *     free, and the event fires only when the server actually said yes.
 *   - Session recording is OFF and event properties never carry user content
 *     (script text, scene names, emails). Scripts are confidential; we record
 *     what people did, not what they wrote.
 *
 * Server-side events (pdf_exported, …) are emitted by backend/services/analytics.py
 * under the same user id, so both sides land on one person in PostHog.
 */
import axios from 'axios';
import apiClient from '@/config/api';
import { EVENTS, KNOWN_EVENTS, eventsForResponse, eventsForError } from './analyticsRules';

export { EVENTS };

const env = (name) =>
  import.meta.env[name] || (typeof window !== 'undefined' && window._env_ && window._env_[name]) || '';

const KEY = env('VITE_POSTHOG_KEY');
const HOST = env('VITE_POSTHOG_HOST') || 'https://us.i.posthog.com';

let client = null;      // posthog instance once loaded
let loading = null;     // in-flight import promise
let failed = false;     // library could not load (ad-blocker, offline) — analytics is off for this page
const queue = [];       // calls made before the library finished loading
const MAX_QUEUE = 100;  // enough for a busy first render; beyond this something is wrong, drop oldest

const run = (fn) => {
  if (!KEY || failed) return;
  if (client) {
    try { fn(client); } catch (e) { /* analytics must never break the app */ }
  } else {
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push(fn);
  }
};

export const isAnalyticsEnabled = () => Boolean(KEY);

export function initAnalytics() {
  if (!KEY || client || loading) return;
  loading = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
        api_host: HOST,
        person_profiles: 'identified_only',   // no anonymous person rows for landing-page traffic
        capture_pageview: 'history_change',   // SPA route changes
        capture_pageleave: true,
        disable_session_recording: true,      // confidential scripts on screen
        mask_all_text: true,                  // autocapture keeps the click, drops the label
        mask_all_element_attributes: true,
      });
      client = posthog;
      queue.splice(0).forEach((fn) => { try { fn(posthog); } catch (e) { /* ignore */ } });
    })
    .catch(() => {                            // blocked by an ad-blocker, offline, … stay silent
      failed = true;                          // …and stop queueing: nothing will ever drain it
      queue.length = 0;
    });
  installApiTracking();
}

export function identifyUser(user) {
  if (!user || !user.id) return;
  run((ph) => ph.identify(user.id, {
    account_type: user.account_type || undefined,
    created_at: user.created_at || undefined,
  }));
}

/** Attach subsequent events to a workspace group so plans can be compared. */
export function setAnalyticsWorkspace(workspace) {
  if (!workspace || !workspace.id) return;
  run((ph) => ph.group('workspace', workspace.id, {
    plan_id: workspace.plan_id || undefined,
    workspace_type: workspace.workspace_type || undefined,
    billing_status: workspace.billing_status || undefined,
  }));
}

export function resetAnalytics() {
  run((ph) => ph.reset());
}

export function track(event, properties = {}) {
  if (import.meta.env.DEV && !KNOWN_EVENTS.has(event)) {
    // eslint-disable-next-line no-console
    console.warn(`[analytics] unknown event "${event}" — add it to EVENTS in lib/analyticsRules.js`);
  }
  run((ph) => ph.capture(event, properties));
}

const fullUrl = (config) => {
  const url = config?.url || '';
  if (/^https?:/i.test(url)) return url;
  return `${config?.baseURL || ''}${url}`;
};

let installed = false;
function installApiTracking() {
  if (installed) return;
  installed = true;
  const onOk = (response) => {
    try {
      eventsForResponse(response.config?.method, fullUrl(response.config), response.data)
        .forEach(({ event, properties }) => track(event, properties));
    } catch (e) { /* never break a request */ }
    return response;
  };
  const onErr = (error) => {
    try {
      eventsForError(error?.response?.status, error?.response?.data)
        .forEach(({ event, properties }) => track(event, properties));
    } catch (e) { /* ignore */ }
    return Promise.reject(error);
  };
  // The app uses both the global axios and the shared apiClient instance.
  axios.interceptors.response.use(onOk, onErr);
  apiClient.interceptors.response.use(onOk, onErr);
}
